import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AiMessage } from '../../models/ai-message.model';
import { AiService } from '../../services/ai.service';
import { AssistantContext, AssistantService } from '../../services/assistant.service';

type ChatState = 'entry' | 'loading' | 'success' | 'error' | 'quota-exceeded';

const CAPABILITY_GREETING: AiMessage = {
  role: 'assistant',
  content: `Hi! I'm your CourseSignage assistant. Here's what I can help you with:\n\n• Add or update holes (distances, par, tee names)\n• Answer questions about your course layout\n• Provide disc golf course design tips\n• Walk you through CourseSignage features\n\nNavigate to a course and ask me anything!`,
  timestamp: new Date(),
  isGreeting: true
};

@Component({
  selector: 'csn-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatComponent implements OnInit, OnDestroy {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;

  chatState: ChatState = 'entry';
  messages: AiMessage[] = [];
  context: AssistantContext = {};
  inputText = '';
  errorMessage = '';

  private destroy$ = new Subject<void>();

  constructor(
    private aiService: AiService,
    private assistantService: AssistantService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.assistantService.messages$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(msgs => {
      this.messages = msgs;
      this.cdr.markForCheck();
    });

    this.assistantService.context$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(ctx => {
      this.context = ctx;
      this.cdr.markForCheck();
    });

    // Seed the greeting when there are no prior messages
    if (this.assistantService.messages.length === 0) {
      this.assistantService.setMessages([CAPABILITY_GREETING]);
    }
  }

  get courseId(): number | undefined { return this.context.courseId; }
  get courseName(): string | undefined { return this.context.courseName; }
  get hasCourse(): boolean { return !!this.context.courseId; }

  close(): void { this.assistantService.close(); }

  sendMessage(): void {
    const text = this.inputText.trim();
    if (!text || this.chatState === 'loading') { return; }
    if (!this.courseId) { return; }

    const userMessage: AiMessage = {
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    const updatedMessages = [...this.messages, userMessage];
    this.assistantService.setMessages(updatedMessages);
    this.inputText = '';
    this.chatState = 'loading';
    this.cdr.markForCheck();

    const history = updatedMessages.slice(0, -1).filter(m => !m.isGreeting);
    this.aiService
      .chat(this.courseId, text, history)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.quotaExceeded) {
            this.chatState = 'quota-exceeded';
          } else {
            const assistantMessage: AiMessage = {
              role: 'assistant',
              content: response.content,
              timestamp: new Date(),
              actions: response.actions
            };
            this.assistantService.setMessages([...this.assistantService.messages, assistantMessage]);
            this.chatState = 'success';
            if (
              response.actions?.includes('add_hole') ||
              response.actions?.includes('update_hole_distance')
            ) {
              this.assistantService.notifyCourseModified(this.courseId!);
            }
          }
          this.focusInput();
          this.cdr.markForCheck();
        },
        error: (err) => {
          if (this.isQuotaError(err)) {
            // Server returns HTTP 403 when the monthly AI-chat cap is hit
            // (body: { error: 'AI quota exceeded', ... }). Show the graceful
            // upsell state instead of a generic error + looping Retry.
            this.chatState = 'quota-exceeded';
          } else {
            this.errorMessage = err?.error?.message ?? 'Something went wrong. Please try again.';
            this.chatState = 'error';
          }
          this.focusInput();
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * True when an HTTP error is the AI-quota cap (403 with a quota-flavored
   * body). The endpoint's only 403 is the monthly cap, so we match defensively
   * on status + message text rather than one exact string.
   */
  private isQuotaError(err: unknown): boolean {
    const e = err as { status?: number; error?: { error?: string; message?: string } };
    if (e?.status !== 403) { return false; }
    const body = e.error;
    const text = `${body?.error ?? ''} ${body?.message ?? ''}`.toLowerCase();
    return text.includes('quota') || !body;
  }

  retry(): void {
    const lastUser = [...this.messages].reverse().find((m) => m.role === 'user');
    if (!lastUser) { return; }
    const historyWithoutLast = this.messages.slice(0, this.messages.lastIndexOf(lastUser));
    this.assistantService.setMessages(historyWithoutLast);
    this.inputText = lastUser.content;
    this.chatState = 'entry';
    this.cdr.markForCheck();
    this.focusInput();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private focusInput(): void {
    setTimeout(() => this.messageInput?.nativeElement.focus(), 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
